import { useStytch, useStytchSession } from '@stytch/nextjs';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { MagicLinksAuthenticateResponse, OAuthAuthenticateResponse } from 'stytch';
import { getWhiteLabelPageTitle } from 'utils/whiteLabelConfig';

import LoadingScreen from 'components/container-components/LoadingScreen';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useUser } from 'context/user';
import { hasSkippedPaymentMethodOnboarding, markPaymentMethodOnboardingSkipped } from 'lib/localStorageConstants';
import { authenticateAdminGrant } from 'services/adminAuthApi';
import { AuthRoutes, ReferralRoutes } from 'routes';
import { cancelAxiosRequests } from 'services/config';
import { associateReferral, referralSignUp } from 'services/referralApi';
import { LS_key_Current_loggedIn_as_RoleType, LS_key_Last_Used_Auth_Method } from 'utils/affiliations';
import { getReferralSlug } from 'utils/referral';
import { captureErrorInDatadog, logError, logStytchError } from 'utils/error';
import {
  clearLSEmailProps,
  dummyEmail,
  setPostHogAndDatadogUser,
  THERA_SkipEmailVerify,
  THERA_SuperAdmin,
  THERA_SupportAdmin,
} from 'utils/user';
import { useTheraFernClient } from 'hooks/useFern';

const OAUTH_TOKEN = 'oauth';
const MAGIC_LINKS_TOKEN = 'magic_links';
const LOGIN_TOKEN = 'login';
export const SESSION_DURATION_MINUTES = 60 * 24 * 3; // 3 days
export const SESSION_REFRESH_INTERVAL = 1000 * 60 * 60 * 24 * 3; // 3 days

const defaultErrorMessage = 'Something went wrong. Redirecting to login page...';

const errorMessageByType = {
  magic_link_not_found: 'The login link you used is invalid or has expired. Redirecting to login page...',
  oauth_token_not_found: 'The login link you used is invalid or has expired. Redirecting to login page...',
  oauth_config_not_found: 'The login method you used is invalid. Redirecting to login page...',
  oauth_user_registration_not_found: 'The login link you used is invalid or has expired. Redirecting to login page...',
};

const authenticationOptions = {
  session_duration_minutes: SESSION_DURATION_MINUTES,
};

type AuthenticationPromise = Promise<MagicLinksAuthenticateResponse> | Promise<OAuthAuthenticateResponse>;
type AuthRes = MagicLinksAuthenticateResponse | OAuthAuthenticateResponse;
type UserProfileType = 'ADMIN' | 'WORKER';

const UserProfileTypeToRoleType = {
  ADMIN: 'manager',
  WORKER: 'worker',
};

const Authenticate = () => {
  const stytch = useStytch();
  const { logout } = useUser();
  const { session } = useStytchSession();
  const { theraFernApi } = useTheraFernClient();
  const router = useRouter();
  const adminGrant = router?.query?.adminGrant?.toString();
  const stytch_token_type = router?.query?.stytch_token_type?.toString();
  const token = router?.query?.token?.toString();
  const email = router?.query?.email?.toString();
  const userProfileType = router?.query?.user_profile_type?.toString() as UserProfileType;
  const redirectAfterAuth = router?.query?.redirect?.toString();

  useEffect(() => {
    // Every navigation below runs after an await, so it can land once the user has already
    // moved on. Replacing then would overwrite the page they reached and take away their way
    // back, so nothing here navigates once the effect has been torn down.
    const pendingRedirects: ReturnType<typeof setTimeout>[] = [];
    let isDisposed = false;
    // Leaving this page starts well before it unmounts, so stop at the first route change too.
    // Waiting for unmount would let a late redirect hijack the destination already in flight.
    const dispose = () => {
      isDisposed = true;
    };
    router.events.on('routeChangeStart', dispose);
    const navigate = (go: () => void) => {
      if (isDisposed) return;
      go();
    };
    // The sign-in redirects are delayed so the user can read the error toast first.
    const scheduleSignInRedirect = (go: () => void) => {
      if (isDisposed) return;
      pendingRedirects.push(setTimeout(() => navigate(go), 3000));
    };

    if (token || adminGrant) {
      (async () => {
        if (adminGrant) {
          // End any old session and clear stale local state before impersonating.
          // getSync() covers the session hook not being hydrated yet.
          if (session || stytch.session.getSync()) {
            try {
              await stytch.session.revoke({ forceClear: true });
            } catch {
              /* ignore */
            }
            await logout(false, false);
          }
          try {
            // Backend makes and redeems the login link with admin_type already on the session.
            const res = await authenticateAdminGrant(adminGrant);
            if (res) {
              stytch.session.updateSession({ session_token: res.sessionToken, session_jwt: res.sessionJwt });
              const sessionRes = await stytch.session.authenticate({
                session_duration_minutes: SESSION_DURATION_MINUTES,
              });
              const adminType = sessionRes?.session?.custom_claims?.admin_type;
              // THERA_SkipEmailVerify is still read in prod by the maintenance-mode bypass.
              if (adminType === 'super') {
                localStorage.setItem(THERA_SuperAdmin, 'true');
                localStorage.setItem(THERA_SkipEmailVerify, dummyEmail);
                window.location.replace('/');
                return;
              } else if (adminType === 'support') {
                localStorage.setItem(THERA_SupportAdmin, 'true');
                window.location.replace('/');
                return;
              }
              // 200 OK but claims not attached revoke to prevent MFA firing for target user
              await stytch.session.revoke({ forceClear: true });
            }
            // No admin session came back; clear anything left behind
            await stytch.session.revoke({ forceClear: true });
          } catch {
            // Network or auth error — revoke any session that may have been created
            try {
              await stytch.session.revoke({ forceClear: true });
            } catch {
              /* ignore */
            }
          }
          theraToast.warning('This link is invalid or expired. Please get a new one.', {
            id: 'admin-grant-failed',
            duration: 10000,
          });
          return;
        }
        if (!token) return;

        const refFromQuery = router?.query?.ref as string;
        const refFromStorage = getReferralSlug();
        const referralSlug = refFromQuery || refFromStorage;

        if (session) {
          // This is a token-exchange logout (e.g. the email-verification magic link clicked
          // mid-onboarding), not a user-initiated sign-out, but logout() still wipes
          // localStorage. Two things have to survive it: the payment-method Skip flag, or the
          // redirect ladder re-gates the user back to /sign-up/add-payment-method right after
          // they verify their email; and the sign-up draft, or they lose everything they had
          // typed into the wizard. Real sign-outs still clear both (by design).
          const hadSkippedPaymentMethodOnboarding = hasSkippedPaymentMethodOnboarding();
          await logout(false, false, true);
          if (hadSkippedPaymentMethodOnboarding) {
            markPaymentMethodOnboardingSkipped();
          }
        }

        let authenticationPromise: AuthenticationPromise;
        if (stytch_token_type === OAUTH_TOKEN) {
          authenticationPromise = stytch.oauth.authenticate(
            token,
            authenticationOptions,
          ) as unknown as AuthenticationPromise;
        } else if (stytch_token_type === MAGIC_LINKS_TOKEN || stytch_token_type === LOGIN_TOKEN) {
          authenticationPromise = stytch.magicLinks.authenticate(
            token,
            authenticationOptions,
          ) as unknown as AuthenticationPromise;
        } else {
          const err = new Error('Invalid stytch_token_type');
          captureErrorInDatadog(err, {
            data: { token, stytch_token_type },
            source: 'Invalid stytch_token_type',
          });
          return;
        }

        if (email) {
          setPostHogAndDatadogUser({ email });
        }

        authenticationPromise
          .then(async (_res: AuthRes) => {
            // Remove query parameters from the URL
            const { pathname } = router;
            const userId = _res?.user_id;
            const email = _res?.user?.emails?.[0]?.email;
            const name = _res?.user?.name?.first_name
              ? _res?.user?.name?.first_name + ' ' + _res?.user?.name?.last_name
              : '';
            setPostHogAndDatadogUser({ userId, email, name });
            cancelAxiosRequests();
            clearLSEmailProps(email);

            // Store the authentication method on successful login
            try {
              if (stytch_token_type === OAUTH_TOKEN) {
                window.localStorage.setItem(LS_key_Last_Used_Auth_Method, 'google');
              } else if (stytch_token_type === MAGIC_LINKS_TOKEN || stytch_token_type === LOGIN_TOKEN) {
                window.localStorage.setItem(LS_key_Last_Used_Auth_Method, 'password');
              }
            } catch (error) {
              logError(error);
            }

            if (userProfileType) {
              const roleType = UserProfileTypeToRoleType[userProfileType];
              try {
                window.localStorage.setItem(LS_key_Current_loggedIn_as_RoleType, roleType);
              } catch (error) {
                logError(error);
              }
            }

            // Associate userId with the pending referral record at auth time.
            // This writes userId to the backend so completeReferral can find it by userId later —
            // no localStorage dependency, works even after 30 days.
            try {
              if (referralSlug && userId) {
                associateReferral(theraFernApi, { slug: referralSlug, userId }).catch(() => undefined);
              }
            } catch {
              // never block auth on referral errors
            }

            try {
              if (redirectAfterAuth?.includes(ReferralRoutes.Portal)) {
                referralSignUp(theraFernApi, {
                  email: email || '',
                  firstName: _res?.user?.name?.first_name || '',
                  lastName: _res?.user?.name?.last_name || '',
                  slug: referralSlug || undefined,
                }).catch(() => undefined);
              }
            } catch {
              // never block auth
            }

            if (redirectAfterAuth && redirectAfterAuth.startsWith('/') && !redirectAfterAuth.startsWith('//')) {
              navigate(() => router.replace(redirectAfterAuth));
            } else {
              navigate(() => router.replace(pathname, pathname, { shallow: true }));
            }
          })
          .catch(async (error) => {
            // Logged before the branching below so every failure path is reported once, carrying
            // the request_id that joins this to the row in Stytch's activity log.
            logStytchError(error, { source: 'authenticate', data: { stytchTokenType: stytch_token_type } });
            // if the email is known, then resend the magic link
            clearLSEmailProps(email);
            if (email) {
              if (session) {
                // revoke existing session here because the send method tries to attach this email to the existing session's user
                await stytch.session.revoke({ forceClear: true });
              }
              let loginUrl = window.location.origin + AuthRoutes.Authenticate + '?email=' + email;
              if (redirectAfterAuth) {
                loginUrl += '&redirect=' + encodeURIComponent(redirectAfterAuth);
              }
              stytch.magicLinks.email
                .send(email, {
                  login_magic_link_url: loginUrl,
                  login_expiration_minutes: SESSION_DURATION_MINUTES,
                  login_template_id: 'login_request',
                })
                .then(() => {
                  theraToast.warning('This link has expired. Please use the new login link sent to your email.', {
                    id: 'authentication-failed',
                    duration: 10000,
                  });
                })
                .catch((err) => {
                  theraToast.error({
                    message: 'Failed to send login link. Please try again or contact support',
                    error: err,

                    options: {
                      id: 'authentication-failed',
                      duration: 4000,
                      sendToSlack: false,
                    },
                  });
                });
              scheduleSignInRedirect(() =>
                router.replace(
                  redirectAfterAuth
                    ? AuthRoutes.SignIn + '?redirect=' + encodeURIComponent(redirectAfterAuth)
                    : AuthRoutes.SignIn,
                ),
              );
              return;
            }
            if (session) {
              return;
            }
            const maybeUsedOrExpiredMagicLink = error?.error_type === 'unable_to_auth_magic_link';

            if (maybeUsedOrExpiredMagicLink) {
              navigate(() => router.replace(AuthRoutes.SignIn));
              theraToast.warning(
                'The login link is invalid. Please enter email below and click on Forgot password to receive new link on email.',
                {
                  id: 'authentication-failed',
                  duration: 10000,
                },
              );
              return;
            }
            theraToast.error({
              message: `[Failed to authenticate]: ${errorMessageByType[error.error_type] || defaultErrorMessage}`,
              options: {
                id: 'authentication-failed',
                duration: 4000,
              },
            });
            scheduleSignInRedirect(() => router.replace(AuthRoutes.SignIn));
          });
      })();
    }

    return () => {
      dispose();
      router.events.off('routeChangeStart', dispose);
      pendingRedirects.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stytch_token_type, token, adminGrant]);

  return (
    <div>
      <Head>
        <title>{getWhiteLabelPageTitle('Authentication | Thera')}</title>
      </Head>
      <LoadingScreen />
    </div>
  );
};

export default Authenticate;
